type AxisName = 'X' | 'Y' | 'Z' | 'A' | 'C';

interface AxisMapping {
  readonly name: AxisName;
  readonly index: number;
  readonly isDangerous: boolean;
}

/**
 * Axis configuration — single source of truth.
 * Backend sends 9-element arrays; active axis indices map per `index` below.
 */
export const AXIS_CONFIG: readonly AxisMapping[] = [
  { name: 'X', index: 0, isDangerous: false },
  { name: 'Y', index: 1, isDangerous: false },
  { name: 'Z', index: 2, isDangerous: true },
  { name: 'A', index: 3, isDangerous: false },
  { name: 'C', index: 5, isDangerous: false },
] as const;

/**
 * Name → array-index lookup, derived from AXIS_CONFIG so components never
 * hardcode raw indices (which is how the A=4 vs A=3 bug crept in).
 */
export const AXIS_INDEX = Object.fromEntries(AXIS_CONFIG.map((a) => [a.name, a.index])) as Record<
  AxisName,
  number
>;

// --- Incoming Payloads (Backend → Frontend) ---

export interface MachineStatusPayload {
  readonly type: 'STATUS';
  /** 'STARTING' = LinuxCNC present but not connected yet (controller booting) */
  readonly mode: 'REAL' | 'SIMULATION' | 'STARTING';
  /** Backend send time (epoch ms) — used for staleness detection */
  readonly ts: number;
  readonly estop: 0 | 1;
  readonly enabled: boolean;
  readonly homed: readonly number[];
  readonly position: readonly number[];
  /** Per-JOINT actual positions (joint-indexed, incl. both Y gantry joints).
   *  Non-zero while homing even when `position` still reads 0 (axis/world
   *  coords are invalid until the machine is referenced). */
  readonly jointPositions: readonly number[];
  /** Work-coordinate (G54..) position with G92 + tool offsets applied — the DRO
   *  primary readout. `position` stays the machine-absolute (G53) value. */
  readonly workPosition: readonly number[];
  readonly motionLine: number;
  readonly programLineCount: number;
  readonly programRunning: boolean;
  readonly programPaused: boolean;
  /** Sequential MDI queue is dispatching staged lines one-by-one */
  readonly mdiRunning: boolean;
  /** 0-based index of the line currently executing (-1 = idle) */
  readonly mdiLine: number;
  /** Total staged lines in the current MDI queue (0 = idle) */
  readonly mdiTotal: number;
  /** A single MDI command (Call Tool / Get Offset) is still executing on the
   *  interpreter — distinct from the mdi* queue fields, which stay idle for it */
  readonly mdiBusy: boolean;
  /** Feed override read-back as a percentage (100 = programmed feed) */
  readonly feedOverride: number;
  /** Spindle-speed override read-back as a percentage (100 = programmed RPM) */
  readonly spindleOverride: number;
  /** Active programmed feed rate (F) in units/min — from LinuxCNC settings[1] */
  readonly feedRate: number;
  /** Active programmed spindle speed (S) in RPM — from LinuxCNC settings[2] */
  readonly spindleSpeed: number;
  /** Spindle on/off — True while the spindle is commanded to turn (M3/M4), False
   *  after M5. Machine authority, so it reflects M3/M5 from any source (the jog
   *  Start/Stop button, MDI or a running program), unlike `spindleSpeed`. */
  readonly spindleOn: boolean;
  /** Active work coordinate offset — 9-element array (X..W), maps via AXIS_INDEX */
  readonly workOffset: readonly number[];
  /** Active work coordinate system name (G54..G59.3) */
  readonly workOffsetName: string;
  /** Tool number currently in the spindle (LinuxCNC tool_in_spindle). 0 = empty.
   *  Authority for the active tool — geometry is resolved from the tool table. */
  readonly toolInSpindle: number;
  /** Tool-table file's nanosecond mtime as a string ("" when the file is absent).
   *  Changes trigger a tool-table re-fetch instead of fixed-interval polling. */
  readonly toolTableVersion: string;
}

/**
 * Error/message from LinuxCNC error_channel().
 * kind maps to LinuxCNC constants:
 *   NML_ERROR = critical hardware/runtime error
 *   OPERATOR_ERROR = operator-level error (G-code syntax etc.)
 *   NML_TEXT = informational system message
 *   OPERATOR_TEXT = informational operator message
 */
export interface ErrorPayload {
  readonly type: 'ERROR';
  readonly kind: 'NML_ERROR' | 'OPERATOR_ERROR' | 'NML_TEXT' | 'OPERATOR_TEXT';
  readonly message: string;
  readonly timestamp: number;
}

/**
 * Command acknowledgement from the backend.
 * ACK  — command was accepted and executed.
 * NACK — command was rejected (validation) or failed (execution); `reason` explains why.
 */
export interface AckPayload {
  readonly type: 'ACK';
  readonly command: string;
  readonly timestamp: number;
}

export interface NackPayload {
  readonly type: 'NACK';
  readonly command: string;
  readonly reason: string;
  readonly timestamp: number;
}

export type IncomingMessage = MachineStatusPayload | ErrorPayload | AckPayload | NackPayload;

// --- Error Entry (stored in Zustand) ---

export interface ErrorEntry {
  readonly id: string;
  readonly kind: ErrorPayload['kind'];
  readonly message: string;
  readonly timestamp: number;
  readonly acknowledged: boolean;
}

/** Critical errors that block the UI and require ACK */
export function isCriticalError(kind: ErrorPayload['kind']): boolean {
  return kind === 'NML_ERROR' || kind === 'OPERATOR_ERROR';
}

// --- Outgoing Commands (Frontend → Backend) ---

export interface HomeCommand {
  readonly type: 'HOME_ALL';
}

export interface JogCommand {
  readonly type: 'JOG';
  readonly axis: number;
  readonly dir: -1 | 0 | 1;
  readonly speed?: number;
  /** Step-size in mm for an increment jog (one press = one step). Omit = continuous. */
  readonly distance?: number;
}

export interface EStopToggleCommand {
  readonly type: 'ESTOP_TOGGLE';
}

export interface RunProgramCommand {
  readonly type: 'RUN_PROGRAM';
  readonly gcode: string;
  readonly filename: string;
}

export interface PauseProgramCommand {
  readonly type: 'PAUSE_PROGRAM';
}

export interface ResumeProgramCommand {
  readonly type: 'RESUME_PROGRAM';
}

export interface StopProgramCommand {
  readonly type: 'STOP_PROGRAM';
}

/** Trigger a fake error for testing the alarm UI (simulation only) */
export interface TestErrorCommand {
  readonly type: 'TEST_ERROR';
}

/** Reset button: force E-Stop — halts all motion and clears queued work. */
export interface MachineEstopCommand {
  readonly type: 'MACHINE_ESTOP';
}

/** Unlock button: clear E-Stop and power the machine on to allow operation. */
export interface MachineUnlockCommand {
  readonly type: 'MACHINE_UNLOCK';
}

/** Set the feed override as a percentage (100 = normal speed). */
export interface FeedOverrideCommand {
  readonly type: 'FEED_OVERRIDE';
  readonly percent: number;
}

/** Set the spindle-speed override as a percentage (100 = programmed RPM). */
export interface SpindleOverrideCommand {
  readonly type: 'SPINDLE_OVERRIDE';
  readonly percent: number;
}

/** Run a single line of G/M-code in MDI (Manual Data Input) mode. */
export interface MDICommand {
  readonly type: 'MDI';
  readonly command: string;
}

/**
 * Submit a batch of staged MDI lines to the machine. The backend paces them
 * one-by-one on INTERP_IDLE (LinuxCNC silently drops mdi() while busy).
 */
export interface RunMdiCommand {
  readonly type: 'RUN_MDI';
  readonly lines: readonly string[];
}

/** Stop button on the Tool loading modal — abort the in-flight single MDI
 *  command (Call Tool / Get Offset) via c.abort(). */
export interface MdiAbortCommand {
  readonly type: 'MDI_ABORT';
}

/**
 * Dev-only: change the backend STATUS broadcast rate live (1-60 Hz). The server
 * default is 20 Hz and resets on restart. Lets the operator slow the stream down
 * while testing on the real machine. Does not touch the machine.
 */
export interface SetBroadcastHzCommand {
  readonly type: 'SET_BROADCAST_HZ';
  readonly hz: number;
}

export type OutgoingCommand =
  | HomeCommand
  | JogCommand
  | EStopToggleCommand
  | RunProgramCommand
  | PauseProgramCommand
  | ResumeProgramCommand
  | StopProgramCommand
  | TestErrorCommand
  | MachineEstopCommand
  | MachineUnlockCommand
  | FeedOverrideCommand
  | SpindleOverrideCommand
  | MDICommand
  | RunMdiCommand
  | MdiAbortCommand
  | SetBroadcastHzCommand;

// --- Connection State ---

export enum ConnectionState {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
}

// --- G-Code File ---

export interface GCodeFile {
  readonly name: string;
  /** Absolute path of the loaded file (falls back to name in the browser) */
  readonly path: string;
  readonly originalLines: readonly string[];
  readonly modifiedLines: readonly string[];
}

/** idle → running → paused ⇄ running → done */
export type RunState = 'idle' | 'running' | 'paused' | 'done';

/** Auto-update lifecycle states */
export type UpdateStatus = 'idle' | 'available' | 'downloading' | 'ready' | 'error';

// --- Phone BLE State ---

export interface BLEFileInfo {
  readonly fileName: string;
  readonly filePath: string;
}

// --- Store State ---

export interface MachineState {
  position: readonly number[];
  /** Per-JOINT actual positions (joint-indexed) — the homing screen's live
   *  readout, non-zero while homing before the machine is referenced. */
  jointPositions: readonly number[];
  /** Work-coordinate (G54..) position incl. G92 + tool offsets — DRO primary. */
  workPosition: readonly number[];
  homed: readonly number[];
  estop: boolean;
  enabled: boolean;
  machineMode: 'REAL' | 'SIMULATION' | 'STARTING' | 'UNKNOWN';
  connectionState: ConnectionState;
  isHomingInProgress: boolean;

  // G-Code
  gcodeFile: GCodeFile | null;
  runState: RunState;
  /**
   * Latched true once the backend confirms a run is actually executing
   * (programRunning). Guards the startup window after Run — until then
   * runState must not fall to 'done' (see updateMachineStatus).
   */
  programConfirmedRunning: boolean;
  motionLine: number;
  programLineCount: number;

  // Sequential MDI queue
  mdiRunning: boolean;
  mdiLine: number;
  mdiTotal: number;
  /** A single MDI command (Call Tool / Get Offset) is executing on the interpreter */
  mdiBusy: boolean;
  /** Sent MDI command lines, kept for the whole app session (survives tab switches) */
  mdiHistory: string[];

  // Override read-back (percent, 100 = programmed)
  feedOverride: number;
  spindleOverride: number;

  // Active programmed F/S read-back (feed in units/min, spindle in RPM)
  feedRate: number;
  spindleSpeed: number;

  // Spindle on/off (machine authority: reflects M3/M5 from any source)
  spindleOn: boolean;

  // Active work coordinate system (from LinuxCNC g5x)
  workOffset: readonly number[];
  workOffsetName: string;

  // Errors / Alarms
  errors: ErrorEntry[];
  /** Durable machine-response transcript (errors + info/debug output) for the MDI
   *  console. Unlike `errors` (which the toast layer prunes after 5s), this survives
   *  tab switches and is only emptied by an explicit Clear. */
  mdiConsole: ErrorEntry[];
  /** True when there are unacknowledged critical errors — blocks UI */
  hasActiveAlarm: boolean;

  // UI preferences (session-only, reset to off on each app launch)
  /** Show the decorative corner triangles inside the analog jog pad */
  showJogPadDeco: boolean;
  /** Selected jog feed rate in mm/min (Feed Rate picker) */
  jogFeedRate: number;
  /** Selected jog step-size in mm — one press moves exactly this far (Step Size picker) */
  jogIncrement: number;
  /**
   * Dev-only: how many G-code rows the preview keeps mounted at once (the
   * virtualization window). Default 17; raising it mounts more rows so the Pi's
   * render cost can be stress-tested. See GCodePreviewModal.
   */
  gcodePreviewWindow: number;

  // Auto-Update
  updateStatus: UpdateStatus;
  downloadProgress: number;
  updateVersion: string;
  /** Currently running app version (from main process) */
  appVersion: string;
  /** Last updater error message, if any */
  updateError: string | null;

  /** Tool number reported in the spindle by LinuxCNC (0 = empty). The authority
   *  for which tool is active — set from STATUS, or optimistically on Call Tool. */
  toolInSpindle: number;
  /** Tool-table file version (nanosecond mtime string; "" = absent). A change
   *  tells ToolsPage to re-fetch the table instead of polling. */
  toolTableVersion: string;
  /** Tool geometry cache keyed by tool number, sourced from the tool table
   *  (/tools). Used to resolve `activeTool`'s type/diameter from the number. */
  toolCatalog: Record<number, ActiveToolInfo>;
  /** The active tool, derived from `toolInSpindle` resolved against `toolCatalog`.
   *  Shown across pages (Jog badge, preview, Tool page). null when the spindle is
   *  empty. Never set directly — recomputed when the number or catalog changes. */
  activeTool: ActiveToolInfo | null;

  // Phone BLE
  phoneBLEConnected: boolean;
  /** Bağlı telefonun BLE MAC/UUID adresi */
  phoneBLEAddress: string | null;
  /** bleno advertising aktif mi */
  phoneBLEAdvertising: boolean;
  /** Son alınan dosya bilgisi */
  lastBLEFile: BLEFileInfo | null;

  // Actions
  readonly updateMachineStatus: (payload: MachineStatusPayload) => void;
  readonly setConnectionState: (state: ConnectionState) => void;
  readonly setHomingInProgress: (inProgress: boolean) => void;
  readonly setWebSocket: (ws: WebSocket | null) => void;
  readonly sendCommand: (command: OutgoingCommand) => void;
  readonly setGcodeFile: (file: GCodeFile | null) => void;
  readonly setRunState: (state: RunState) => void;
  readonly addMdiHistory: (line: string) => void;
  readonly clearMdiHistory: () => void;
  readonly addError: (payload: ErrorPayload) => void;
  readonly acknowledgeError: (errorId: string) => void;
  readonly clearInfoMessages: () => void;
  readonly clearMdiConsole: () => void;

  // UI preference actions
  readonly setShowJogPadDeco: (show: boolean) => void;
  readonly setJogFeedRate: (rate: number) => void;
  readonly setJogIncrement: (increment: number) => void;
  readonly setGcodePreviewWindow: (count: number) => void;

  // Auto-Update actions
  readonly setUpdateStatus: (status: UpdateStatus) => void;
  readonly setDownloadProgress: (percent: number) => void;
  readonly setUpdateVersion: (version: string) => void;
  readonly setAppVersion: (version: string) => void;
  readonly setUpdateError: (message: string | null) => void;

  // Phone BLE actions
  readonly setPhoneBLEConnected: (connected: boolean, address?: string) => void;
  readonly setPhoneBLEAdvertising: (active: boolean) => void;
  readonly setLastBLEFile: (file: BLEFileInfo | null) => void;
  /** Optimistically set the spindle tool number (e.g. on Call Tool) — STATUS
   *  from the machine confirms or corrects it. */
  readonly setToolInSpindle: (toolNo: number) => void;
  /** Replace the tool geometry cache (from a /tools fetch). */
  readonly setToolCatalog: (catalog: Record<number, ActiveToolInfo>) => void;
  /** Fetch the tool table and refresh the geometry cache. Resolves true when the
   *  fetch succeeded (so a cold-boot caller can retry until the backend is up). */
  readonly refreshToolCatalog: () => Promise<boolean>;
}

/** Minimal shape the Jog badge needs to render the active tool. Kept here (not
 *  a ToolRow) so the core types stay decoupled from the Tool page component. */
export interface ActiveToolInfo {
  no: string;
  /** Tool-type name (e.g. "End Mill"); '' for an unnamed/empty pocket. */
  type: string;
  /** Diameter as stored on the row (e.g. "6.00 mm"). */
  diameter: string;
}

// --- Constants ---

export const WS_URL = 'ws://127.0.0.1:8000/ws';

/** Loopback backend HTTP base — same host as the WS control channel. Shared by
 *  the tool-table, probe/measurement, camera and assistant fetch helpers. */
export const API_BASE = 'http://127.0.0.1:8000';

/**
 * Web/demo mode: no backend reachable (e.g. Cloudflare Pages deploy). When set,
 * the app skips the WebSocket + HTTP calls to the loopback backend entirely and
 * renders the UI in a static, unlocked state. Enabled at build time via
 * `VITE_DEMO=1`. Off (false) for the normal Electron/Pi build.
 */
export const DEMO_MODE = import.meta.env.VITE_DEMO === '1';

/**
 * Homing is per-JOINT, and the `homed` array is joint-indexed — unlike
 * `position`/`workPosition`, which are axis-letter-indexed (one Y). This
 * machine's [TRAJ] COORDINATES is "X Y Y Z A C": the Y gantry is two joints
 * (Y1, Y2), which shifts Z/A/C in the homed array relative to the axis letters.
 * Joints 6 and 7 are the ATC (tool changer) linear and rotary joints — they are
 * part of the home sequence too, so they must be referenced before unlock.
 * Single source of truth for that joint layout — the homing display and the
 * unlock check both derive from it so they can't drift.
 *   `label`      — operator-facing name (Y1/Y2 are the two gantry joints)
 *   `homedIndex` — joint number, i.e. the index into the `homed` and
 *                  `jointPositions` arrays
 */
export interface HomingJoint {
  readonly label: string;
  readonly homedIndex: number;
}

export const HOMING_JOINTS: readonly HomingJoint[] = [
  { label: 'X', homedIndex: 0 },
  { label: 'Y1', homedIndex: 1 },
  { label: 'Y2', homedIndex: 2 },
  { label: 'Z', homedIndex: 3 },
  { label: 'A', homedIndex: 4 },
  { label: 'C', homedIndex: 5 },
  { label: 'ATC lin', homedIndex: 6 },
  { label: 'ATC rot', homedIndex: 7 },
] as const;

/**
 * Joint indices into `homed` that must all be referenced before the machine
 * unlocks. Derived from HOMING_JOINTS so the lock and the homing display can't
 * drift. On this gantry index 4 is the A joint (not a phantom slot), so it is
 * included — the machine stays locked until A is homed too.
 */
export const HOMED_JOINT_INDICES: readonly number[] = HOMING_JOINTS.map((j) => j.homedIndex);

/** Auto-dismiss delay for info toasts (ms) */
export const TOAST_DISMISS_DELAY = 5000;

// ============================================================================
// Electron API Type Declaration (preload bridge)
// ============================================================================

interface UpdaterAPI {
  onUpdateAvailable: (cb: (info: { version: string; releaseNotes: string }) => void) => void;
  onUpdateNotAvailable: (cb: () => void) => void;
  onDownloadProgress: (
    cb: (progress: {
      percent: number;
      bytesPerSecond: number;
      transferred: number;
      total: number;
    }) => void,
  ) => void;
  onUpdateDownloaded: (cb: () => void) => void;
  onUpdateError: (cb: (message: string) => void) => void;
  startDownload: () => void;
  quitAndInstall: () => void;
  getAppVersion: () => Promise<string>;
  removeAllUpdateListeners: () => void;
}

interface WindowControlsAPI {
  isFullscreen: () => Promise<boolean>;
  toggleFullscreen: () => Promise<boolean>;
}

interface PhoneBLEAPI {
  onConnect: (cb: (data: { address: string }) => void) => void;
  onDisconnect: (cb: (data: { address: string }) => void) => void;
  onAdvertising: (cb: (data: { active: boolean }) => void) => void;
  onFileReceived: (cb: (data: { fileName: string; filePath: string }) => void) => void;
  removeAllListeners: () => void;
}

export interface ElectronAPI {
  send: (channel: string, data?: unknown) => void;
  on: (channel: string, callback: (...args: unknown[]) => void) => void;
  invoke: (channel: string, data?: unknown) => Promise<unknown>;
  getPathForFile?: (file: File) => string;
  updater?: UpdaterAPI;
  phoneBLE?: PhoneBLEAPI;
  windowControls?: WindowControlsAPI;
}

declare global {
  interface Window {
    electronAPI?: ElectronAPI;
  }
}
